/* Core & libraries */
import React, { useEffect, useState } from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

/* Helpers, constants, custom methods, internal libraries */
/* Components */
import { ReactComponent as UserDeleteIcon } from "../../../../resources/images/user-x.svg";
import UCGrid from "../../../ucgrid";
import DeleteActionModal from "../../../modals/deleteActionModal";
import TeamDetailsNoMembers from "../../../../components/teamDetailsNoMembers";
import UpdateTeamActionModal from '../../../modals/updateTeamActionModal';
import WarningModal from '../../../modals/warningModal';
import UCInput from '../../../ucinput';
import { TRAVELER_DETAILS_ROUTE } from "../../../../constants/routes";
import { useUserService } from "../../../../services/userService";
import { ReactComponent as UpdateTeamIcon } from "../../../../resources/images/people.svg";
import { useLocaleContext } from '../../../../contexts/localeContext';
import { dateFormatWithLocale } from '../../../../helpers/general';

/* Styles */
import styles from "./styles.js"

const messages = defineMessages({
  removePlaceholder: {
    id: 'admin.remove.lastATM.removePlaceholder'
  },
})

const MembersGrid = (props) => {
  let { teamId } = useParams();
  const {gridOptions, setGridOptions, members, deleteMember, teamDetails, updatePeople, gridActionPermision} = props;
  const theme = useTheme();
  const classes = styles({ theme });
  const { userDetails } = useUserService();
  const navigate = useNavigate();
  const locale = useLocaleContext();
  const intl = useIntl();

  const [selectedRow, setSelectedRow] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpdateTeamModal, setShowUpdateTeamModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showRemoveCitiOnlyModal, setShowRemoveCitiOnlyModal] = useState(false);
  const [showDeleteLastAtmModal, setShowDeleteLastAtmModal] = useState(false);
  const [permissionsResponse, setGridPermissionsResponse] = useState({});
  const [removeInput, setRemoveInput] = useState("");

  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const gridSpinnerName = "membersGridSpinner"

  useEffect(() => {
    if(!showDeleteLastAtmModal) {
      setRemoveInput("")
    }
  }, [showDeleteLastAtmModal])

  const getDeleteContent = () => (
    <>
      <div className={classes.deleteActionDescription}><FormattedMessage id="admin.remove.lastATM.description" /></div>
      <div className={classes.deleteActionNote}><FormattedMessage id="admin.remove.lastATM.note" /></div>
      <UCInput
        inputClassName={classes.removeInput}
        placeholder={intl.formatMessage(messages.removePlaceholder)}
        onChange={event => setRemoveInput(event.target.value)}
      />
    </>
  )

  const disableRemove = () => {
    const word = intl.formatMessage(messages.removePlaceholder);
    return removeInput !== word;
  }

  const actionCallback = async (actionType, row = null) => {
    const response = await gridActionPermision(row.entityID, `${gridSpinnerName}.${actionType}Action`)
    if (response) {
      setGridPermissionsResponse(response)
      switch (actionType) {
        case "delete":
           if (userDetails?.manageCompany && response.lastAdmin) {
            setShowDeleteLastAtmModal(true)
          } else if (response.canRemove) {
            setShowDeleteModal(true)
          } else {
            setShowRemoveCitiOnlyModal(true)
          }
          break;
        case "edit":
           response.canChangeRole ?
              setShowUpdateTeamModal(true) :
              setShowRemoveCitiOnlyModal(true)
          break;
      }
    }
  }

  const deleteAction = async () => {
    const response = await deleteMember(selectedRow)
    if (response) {
      setShowDeleteModal(false)
    }
  }

  const deleteLastATMAction = async () => {
    const response = await deleteMember(selectedRow)
    if (response) {
      setShowDeleteLastAtmModal(false)
    }
  }

  const updateAction = async () => {
    const values = {
      userId: selectedRow.entityID,
      firstName:selectedRow.firstName,
      lastName: selectedRow.lastName,
      roleName: selectedRow.roleName,
      roleId: selectedOption
    }

    const response = await updatePeople(values)
    if (response) {
      setShowUpdateTeamModal(false)
    }
  }

  return (
    <div className={`${classes.membersGrid} ${members?.actions?.length === 0 ? classes.noActionsStyle : ""}`}>
      <UCGrid
        gridName="membersGrid"
        data={ members?.results }
        columns={ members?.columns }
        actions={ members?.actions }
        handlePageChange={(page) => {setGridOptions({
          ...gridOptions,
          page
        })
        page > 1 ? navigate(`/app/teams/${teamId}?page=${page}`) : navigate(`/app/teams/${teamId}`)
        }}
        setSelectedRow={setSelectedRow}
        actionCallback={actionCallback}
        spinner={gridSpinnerName}
        customColumns={{
          roleName: (row) => (
            <div className={classes.roleName}><FormattedMessage id={`roles.${row.roleId}.${providerCode}`} /></div>
          ),
          createdDateTime: (row) => (
            dateFormatWithLocale(row.createdDateTime, locale)
          ),
        }}
        avatarNameLink={row => TRAVELER_DETAILS_ROUTE.replace(":travelerId", row.entityID)}
        defaultSort={members?.defaultSort}
        handleSort={(column, sortDirection) =>
          setGridOptions({
            ...gridOptions,
            sortby: column.id,
            sortdirection: sortDirection === "desc" ? "dsc" : sortDirection,
          })
        }
        noDataComponent={ <TeamDetailsNoMembers teamDetails={teamDetails}/> }
        onRowClickRoute={row => TRAVELER_DETAILS_ROUTE.replace(":travelerId", row.entityID)}
      />

      {
        members?.actions && members.actions.map((action,i) => {
          switch (action) {
            case "delete":
              return  <React.Fragment key={`${action}-${i}`}>
                <DeleteActionModal
                  showModal={showDeleteModal}
                  setShowModal={setShowDeleteModal}
                  setSelectedRow={setSelectedRow}
                  modalIcon={ <UserDeleteIcon /> }
                  modalTitle={ <FormattedMessage id="peopleGrid.deleteAction.title" values={{name: `${selectedRow?.firstName} ${selectedRow?.lastName}`}}/> }
                  modalDescription={ <FormattedMessage id="peopleGrid.deleteAction.description" /> }
                  submitCallback={deleteAction}
                />

                <DeleteActionModal
                  showModal={showDeleteLastAtmModal}
                  setShowModal={ setShowDeleteLastAtmModal }
                  modalIcon={ <UserDeleteIcon /> }
                  modalTitle={ <FormattedMessage id="admin.remove.lastATM.title" values={{name: `${selectedRow?.firstName} ${selectedRow?.lastName}`}}/> }
                  modalDescription={ getDeleteContent() }
                  submitCallback={ deleteLastATMAction }
                  setSelectedRow={(value) => console.log(value)}
                  disableSubmit={ disableRemove() }
                  submitText={ <FormattedMessage id="peopleGrid.deleteAction.remove"/> }
                />
            </React.Fragment>
             case "edit":
            return <UpdateTeamActionModal
                showModal={showUpdateTeamModal}
                setShowModal={setShowUpdateTeamModal}
                setSelectedRow={setSelectedRow}
                selectedRow={selectedRow}
                role={selectedRow?.roleId}
                setSelectedOption={setSelectedOption}
                modalIcon={ <UpdateTeamIcon /> }
                modalTitle={ <FormattedMessage id="peopleGrid.editAction" />  }
                modalDescription={ <FormattedMessage id="peopleGrid.updateTeamAction.description" values={{name: `${selectedRow?.firstName} ${selectedRow?.lastName}`}}/> }
                submitCallback={updateAction}
                key={`${action}-${i}`}
              />
          }
        })
      }

      <WarningModal
        showModal={showRemoveCitiOnlyModal}
        setShowModal={setShowRemoveCitiOnlyModal}
        modalIcon={ <UserDeleteIcon /> }
        modalTitle={ <FormattedMessage id="peopleGrid.citiUserWarning.title"/> }
        modalDescription={ permissionsResponse.isCiti ? <FormattedMessage id="peopleGrid.citiUserWarning.description" /> : <FormattedMessage id="peopleGrid.citiUserWarning.notCiti.description" />}
      />
    </div>
  )
}

export default MembersGrid;
